import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LOCATIONS } from "@/lib/suppliers";
import { CalendarRange } from "lucide-react";

/**
 * Weekly Liabilities aggregation — pulls from the backend Python endpoint
 * /api/portal/cheques/weekly. Filterable by kiosk. `refreshKey` is bumped
 * by the parent on every cheque mutation (add / edit / delete) to force
 * live recalculation.
 */
export default function WeeklyLiabilities({ refreshKey = 0 }) {
  const [location, setLocation] = useState("ALL");
  const [data, setData] = useState({ weeks: [], grand_total: 0, total_cheques: 0, location: "ALL" });

  useEffect(() => {
    const fetchWeekly = () => {
      const params = location !== "ALL" ? { location } : {};
      api.get("/portal/cheques/weekly", { params })
        .then((r) => setData(r.data || { weeks: [], grand_total: 0 }))
        .catch(() => setData({ weeks: [], grand_total: 0, total_cheques: 0 }));
    };
    fetchWeekly();
    // Live recalculation hook — Owner sees totals updated within 15 s of any
    // add / edit / delete elsewhere in the app.
    const i = setInterval(fetchWeekly, 15000);
    const onFocus = () => fetchWeekly();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(i);
      window.removeEventListener("focus", onFocus);
    };
  }, [location, refreshKey]);

  return (
    <Card className="p-5" data-testid="weekly-liabilities">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <CalendarRange className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Weekly Liabilities · Εβδομαδιαίες Υποχρεώσεις</h3>
            <p className="text-[11px] text-slate-500 tracking-wide">
              Live total of maturing cheques per ISO week
            </p>
          </div>
        </div>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="w-[200px]" data-testid="weekly-location-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Stores · Όλα τα Καταστήματα</SelectItem>
            {LOCATIONS.map((l) => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 text-center" data-testid="weekly-grand-totals">
        <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Grand Total</div>
          <div className="font-mono font-bold text-2xl text-slate-900" data-testid="weekly-grand-total">
            €{data.grand_total.toLocaleString("en-CY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Cheques</div>
          <div className="font-mono font-bold text-2xl text-slate-900" data-testid="weekly-cheque-count">
            {data.total_cheques}
          </div>
        </div>
      </div>

      {data.weeks.length === 0 ? (
        <div
          className="text-sm text-slate-400 text-center py-6 border-2 border-dashed border-slate-200 rounded-lg"
          data-testid="weekly-empty"
        >
          No upcoming cheque liabilities for{" "}
          {location === "ALL" ? "any store" : location}.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="weekly-list">
          {data.weeks.map((w) => (
            <div
              key={w.week_key}
              data-testid={`weekly-${w.week_key}`}
              className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg px-4 py-3"
            >
              <div className="min-w-0">
                <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  Week {w.week} · {w.year}
                  <Badge className="text-[10px] bg-slate-200 text-slate-700 border-slate-300 font-mono">
                    {w.count} cheque{w.count !== 1 ? "s" : ""}
                  </Badge>
                </div>
                <div className="text-[11px] font-mono text-slate-500">
                  {w.week_start} → {w.week_end}
                </div>
              </div>
              <div className="font-mono font-bold text-lg text-orange-700 shrink-0 ml-3">
                €{Number(w.total).toLocaleString("en-CY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
